import React, { FC, useCallback, useEffect, useRef } from "react";
import { LinearProgress, useDialogShadow } from "@evergis/ui";
import { useTranslation } from "react-i18next";
import { BaseTaskDataStorageDc, UploadRasterTaskParametersDc } from "@evergis/api";
import { usePostgresLayer } from "../../../../hooks/layer/usePostgresLayer";
import { updateTileLayer } from "../../../../resources/layers";
import { updateTable } from "../../../../resources/tables";

import { DialogActions } from "../../DialogComponents/DialogActions";
import { DialogTitle } from "../../DialogComponents/DialogTitle";
import { FullScreenDialog, useDialogContext } from "../../DialogComponents/FullScreenDialog";
import { UploadWizard } from "./UploadWizard";
import { UploadFinish } from "./UploadFinish";
import { DialogContent, RestrictedWidthContainer } from "../../DialogComponents/styled";
import { UploadProgressTitle, UploadReadyContainer } from "../styled";
import { useUploadDataContext } from "../uploadDataContext";
import { useDialogInfo, useDialogReset } from "../../../../hooks/useDialogs";
import { useProgress } from "../../../../hooks/scheduler/useProgress";
import { cancelTask, tilingTask } from "../../../../resources/scheduler";
import { useContext } from "../hooks/useContext";
import { useDefaultScaleRange } from "../../../../hooks/style/useDefaultScaleRange";

// eslint-disable-next-line max-lines-per-function
export const UploadTiling: FC = () => {
  const { t } = useTranslation("dialogs");
  const context = useContext();
  const { resources } = useDialogInfo();
  const { applyText } = useDialogContext();
  const { closeDialog } = useDialogInfo();
  const defaultScaleRange = useDefaultScaleRange();
  const { prevStep, uploadedFiles, uploadAttributes, maxZoomLevel, sysAttributes } = useUploadDataContext();
  const { shadows, setScrollContainer } = useDialogShadow();
  const [runTilingTask, { data: taskResult }] = tilingTask.use();
  const layerName = resources[0];
  const { postgresLayer, loading: layerLoading } = usePostgresLayer(sysAttributes.length > 0 ? layerName : "", {
    waitInput: true,
  });
  const [updateTableMutation] = updateTable.use();
  const [updateTileLayerMutation] = updateTileLayer.use();
  const { isTaskCompleted, result, isTaskFailed } = useProgress(taskResult?.taskId);
  const taskRan = useRef(false);
  const cancelDialog = useDialogReset();
  const [cancelTileTask] = cancelTask.use();
  const onCancel = useCallback(async () => {
    if (taskResult?.taskId && !isTaskCompleted) {
      await cancelTileTask({ input: taskResult?.taskId });
    }
    cancelDialog();
  }, [cancelDialog, cancelTileTask, taskResult, isTaskCompleted]);
  const useCog = maxZoomLevel === undefined;

  useEffect(() => {
    if (sysAttributes.length === 0 && !taskRan.current) {
      taskRan.current = true;

      runTilingTask({
        input: {
          target: {
            type: "tileFeatureLayerTaskDataStorage",
            serviceName: layerName,
            createNewService: false,
          } as BaseTaskDataStorageDc,
          attributes: uploadAttributes,
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          files: uploadedFiles.map(file => file.resourceId),
          polygonize: false,
          useCog,
          maxZoomLevel,
        } as UploadRasterTaskParametersDc,
      });
    }

    if (layerLoading || taskRan.current) {
      return;
    }

    (async () => {
      taskRan.current = true;

      const columnsAdd = sysAttributes.map(({ attributeName, type }) => ({ name: attributeName, type }));

      await updateTableMutation({ input: { name: postgresLayer.tableName, columnsAdd } });
      const { configuration: resultLayerConfiguration, name } = postgresLayer;
      const { attributesConfiguration: resultAttributesConfiguration } = resultLayerConfiguration;

      await updateTileLayerMutation({
        input: {
          defaultScaleRange,
          configuration: {
            ...postgresLayer,
            name,
            attributesConfiguration: {
              ...resultAttributesConfiguration,
              attributes: [
                ...resultAttributesConfiguration.attributes,
                ...sysAttributes.map(({ id, sysAttribute, ...attribute }) => attribute),
              ],
            },
          },
        },
      });

      await runTilingTask({
        input: {
          target: {
            type: "tileFeatureLayerTaskDataStorage",
            serviceName: layerName,
            createNewService: false,
          } as BaseTaskDataStorageDc,
          attributes: uploadAttributes,
          files: uploadedFiles.map(file => file.name),
          polygonize: false,
          useCog,
          maxZoomLevel,
        } as UploadRasterTaskParametersDc,
      });
    })();
  }, [layerLoading]);

  return (
    <FullScreenDialog dialogContext={context}>
      <DialogTitle displayShadow={shadows.upper} />
      <DialogContent ref={setScrollContainer}>
        <RestrictedWidthContainer>
          <UploadWizard currentStep={1} disabledStep={isTaskCompleted && !isTaskFailed ? 1 : 0} />
          {isTaskCompleted && (
            <UploadReadyContainer>
              <UploadFinish error={isTaskFailed && result?.message} />
            </UploadReadyContainer>
          )}
          {!isTaskCompleted && (
            <>
              <UploadProgressTitle>
                {useCog ? t("uploadData.formingCOG") : t("uploadData.creatingTiles")}&hellip;
              </UploadProgressTitle>
              <LinearProgress style={{ marginBottom: "7rem" }} />
            </>
          )}
        </RestrictedWidthContainer>
      </DialogContent>
      <DialogActions
        displayShadow={shadows.bottom}
        applyDisabled={!isTaskCompleted || isTaskFailed}
        applyText={applyText}
        onApply={closeDialog}
        onBackButton={isTaskFailed && prevStep}
        onCancel={onCancel}
      />
    </FullScreenDialog>
  );
};
